import { resolveWorkspaceTimeZone } from '../repeat/keys.js'

export interface AutomationPlaceholderContext {
  timeZone?: string
  payload?: Record<string, unknown>
  item?: Record<string, unknown>
  checklist?: { title?: string }
  node?: { title?: string }
  form?: { title?: string }
  fieldCap?: number
  maxChars?: number
}

const BLOCKED_PROPS = new Set(['__proto__', 'constructor', 'prototype'])

const REMOVED_MARKER = '\uE000'
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

function isTimestampLike(val: unknown): val is { toDate?: () => Date; seconds: number } {
  return (
    typeof val === 'object' &&
    val !== null &&
    (('seconds' in val && typeof (val as { seconds: unknown }).seconds === 'number') ||
      ('toDate' in val && typeof (val as { toDate: unknown }).toDate === 'function'))
  )
}

function formatDateInZone(date: Date, tz: string): string {
  const timeZone = resolveWorkspaceTimeZone(tz)
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(date)
}

function formatSingleValue(val: unknown, tz: string): string | null {
  if (val === null || val === undefined) return null
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return Number.isFinite(val) ? String(val) : null
  if (val instanceof Date) {
    return Number.isFinite(val.getTime()) ? formatDateInZone(val, tz) : null
  }
  if (isTimestampLike(val)) {
    const d = typeof val.toDate === 'function' ? val.toDate() : new Date(val.seconds * 1000)
    return Number.isFinite(d.getTime()) ? formatDateInZone(d, tz) : null
  }
  if (typeof val === 'string') {
    const trimmed = val.trim()
    if (!trimmed) return null
    if (DATE_ONLY_PATTERN.test(trimmed)) {
      const parsedMs = Date.parse(`${trimmed}T00:00:00Z`)
      if (Number.isFinite(parsedMs)) {
        return formatDateInZone(new Date(parsedMs), 'UTC')
      }
    }
    if (ISO_DATE_PATTERN.test(trimmed)) {
      const parsedMs = Date.parse(trimmed)
      if (Number.isFinite(parsedMs)) {
        return formatDateInZone(new Date(parsedMs), tz)
      }
    }
    return trimmed
  }
  return null
}

function formatValue(val: unknown, tz: string): string | null {
  if (Array.isArray(val)) {
    const formattedElements = val
      .map((item) => formatSingleValue(item, tz))
      .filter((item): item is string => item !== null)
    return formattedElements.length > 0 ? formattedElements.join(', ') : null
  }
  return formatSingleValue(val, tz)
}

/**
 * Pure placeholder renderer for automation text fields.
 * Replaces {{payload.<key>}}, {{checklist.title}}, {{node.title}}, {{form.title}}.
 * Caches unresolved tokens, rejects code / prototype injection, cleans up leftovers.
 */
export function renderAutomationText(
  text: string,
  ctx?: AutomationPlaceholderContext,
  fieldCap?: number,
): { text: string; unresolved: string[] } {
  if (!text || typeof text !== 'string') {
    return { text: '', unresolved: [] }
  }

  const tz = ctx?.timeZone ?? 'UTC'
  const unresolved: string[] = []
  let tokenWasRemoved = false

  const rendered = text.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (_match, rawToken: string) => {
    const token = rawToken.trim()

    // Blocked prototype properties anywhere in the token
    const segments = token.split('.')
    if (segments.some((seg) => BLOCKED_PROPS.has(seg))) {
      unresolved.push(token)
      tokenWasRemoved = true
      return ''
    }

    // Explicitly unhandled/out-of-scope prefixes: role.*, workspace.*
    if (segments[0] === 'role' || segments[0] === 'workspace') {
      unresolved.push(token)
      tokenWasRemoved = true
      return REMOVED_MARKER
    }

    // Only 2 segments allowed: payload.<key> or item.<key> or <target>.title
    if (segments.length !== 2) {
      unresolved.push(token)
      tokenWasRemoved = true
      return REMOVED_MARKER
    }

    const [scope, key] = segments

    if (scope === 'checklist' && key === 'title') {
      const title = ctx?.checklist?.title?.trim()
      if (title) return title.slice(0, 200)
      unresolved.push(token)
      tokenWasRemoved = true
      return REMOVED_MARKER
    }

    if (scope === 'node' && key === 'title') {
      const title = ctx?.node?.title?.trim()
      if (title) return title.slice(0, 200)
      unresolved.push(token)
      tokenWasRemoved = true
      return REMOVED_MARKER
    }

    if (scope === 'form' && key === 'title') {
      const title = ctx?.form?.title?.trim()
      if (title) return title.slice(0, 200)
      unresolved.push(token)
      tokenWasRemoved = true
      return REMOVED_MARKER
    }

    if (scope === 'item') {
      const itemObj = ctx?.item
      if (
        itemObj &&
        typeof itemObj === 'object' &&
        Object.prototype.hasOwnProperty.call(itemObj, key)
      ) {
        const val = itemObj[key]
        const formatted = formatValue(val, tz)
        if (formatted !== null && formatted !== '') {
          return formatted.slice(0, 200)
        }
      }
      unresolved.push(token)
      tokenWasRemoved = true
      return REMOVED_MARKER
    }

    if (scope === 'payload') {
      const payloadObj = ctx?.payload
      if (
        payloadObj &&
        typeof payloadObj === 'object' &&
        Object.prototype.hasOwnProperty.call(payloadObj, key)
      ) {
        const val = payloadObj[key]
        const formatted = formatValue(val, tz)
        if (formatted !== null && formatted !== '') {
          return formatted.slice(0, 200)
        }
      }
      unresolved.push(token)
      tokenWasRemoved = true
      return REMOVED_MARKER
    }

    unresolved.push(token)
    tokenWasRemoved = true
    return REMOVED_MARKER
  })

  let cleaned = rendered
  if (tokenWasRemoved) {
    // 1. Drop empty "()" leftovers only where token was removed
    cleaned = cleaned.replace(/\s*\(\s*(?:\uE000\s*)+\)/g, '')
    // 2. Drop empty ": ." leftovers only where token was removed
    cleaned = cleaned.replace(/:\s*(?:\uE000\s*)*\./g, '')
    // 3. Drop marker at string boundaries
    cleaned = cleaned.replace(/^[ \t]*\uE000+[ \t]*/g, '')
    cleaned = cleaned.replace(/[ \t]*\uE000+[ \t]*$/g, '')
    // 4. Collapse multiple spaces around removed token
    cleaned = cleaned.replace(/[ \t]+\uE000+[ \t]*/g, ' ')
    cleaned = cleaned.replace(/[ \t]*\uE000+[ \t]+/g, ' ')
    // 5. Strip any leftover markers
    cleaned = cleaned.replace(/\uE000+/g, '')
    cleaned = cleaned.trimEnd()
  }

  const cap = fieldCap ?? ctx?.fieldCap ?? ctx?.maxChars
  if (typeof cap === 'number' && cap >= 0 && cleaned.length > cap) {
    cleaned = cleaned.slice(0, cap)
  }

  return {
    text: cleaned,
    unresolved: Array.from(new Set(unresolved)),
  }
}
